import { useEffect } from 'react';
import { useSocketStore } from '../../stores/useSocketStore';
import { useClientsStore } from '../../stores/useClientsStore';
import { useLoadingStore } from '../../stores/useLoadingStore';

import {
    loadClients,
    finishClientsRequest,
    clearClientsRequests,
    isClientsRequestActive,
    CLIENTS_RETRY_DELAY
} from '../../services/clientsService';

import { loadVehicles } from '../../services/vehiclesService';

// =========================
// REALTIME PENDENTE
// =========================

const pendingClientEvents = [];

let pendingInvalidate = false;

export function useClientsSocket() {

    const socket = useSocketStore(state => state.socket);
    const connectionReady =
        useSocketStore(
            state => state.connectionReady
        );

    useEffect(() => {

        if (!socket) {
            return;
        }

        const {
            setClientes,
            upsertCliente,
            removeCliente
        } = useClientsStore.getState();

        const {
            setLoadProgress
        } = useLoadingStore.getState();

        // =========================
        // PROGRESSO
        // =========================

        const handleProgress = ({
            requestId,
            percentage
        }) => {

            if (!isClientsRequestActive(requestId)) {
                return;
            }

            setLoadProgress(percentage ?? 0);

        };

        // =========================
        // DONE
        // =========================

        const handleDone = ({
            requestId,
            clientes
        }) => {

            const isActive = isClientsRequestActive(requestId);

            if (!isActive) {
                return;
            }

            // =========================
            // INSTALA CLIENTES
            // =========================

            setClientes(clientes || []);

            // =========================
            // FINALIZA REQUEST
            // =========================

            setLoadProgress(100);

            finishClientsRequest(requestId);

            // =========================
            // AINDA EXISTEM CLIENTES
            // SENDO CARREGADOS
            // =========================

            const {
                loadingClients
            } = useLoadingStore.getState();

            if (loadingClients) {
                return;
            }

            // =========================
            // APLICA REALTIME PENDENTE
            // =========================

            flushPendingEvents();

            // =========================
            // INVALIDATE PENDENTE
            // =========================

            if (pendingInvalidate) {

                pendingInvalidate = false;

                loadClients(socket);

                return;

            }

            // =========================
            // PRÓXIMA ETAPA
            // =========================

            loadVehicles(socket);

        };

        // =========================
        // ERROR
        // =========================

        const handleError = ({
            requestId
        }) => {

            const isActive =
                isClientsRequestActive(requestId);

            finishClientsRequest(requestId);

            if (!isActive) {
                return;
            }

            console.error(
                'Erro ao carregar clientes'
            );

            setLoadProgress(100);

            setTimeout(() => {

                const {
                    connectionReady
                } = useSocketStore.getState();

                if (
                    socket?.connected &&
                    connectionReady
                ) {
                    loadClients(socket);
                }

            }, CLIENTS_RETRY_DELAY);

        };

        // =========================
        // APLICA EVENTO REALTIME
        // =========================

        const applyEvent = ({
            type,
            payload
        }) => {

            switch (type) {

                case 'sync':

                    setClientes(payload || []);

                    break;

                case 'upsert':

                    upsertCliente(payload);

                    break;

                case 'delete':

                    removeCliente(payload);

                    break;

                default:
                    break;

            }

        };

        // =========================
        // RECEBE EVENTO REALTIME
        // =========================

        const handleEvent = (
            type,
            payload
        ) => {

            const {
                loadingClients
            } = useLoadingStore.getState();

            if (loadingClients) {

                pendingClientEvents.push({
                    type,
                    payload
                });

                return;
            }

            applyEvent({
                type,
                payload
            });

        };

        // =========================
        // FLUSH REALTIME
        // =========================

        const flushPendingEvents = () => {

            if (!pendingClientEvents.length) {
                return;
            }

            const events =
                pendingClientEvents.splice(
                    0,
                    pendingClientEvents.length
                );

            for (const event of events) {

                applyEvent(event);

            }

        };

        // =========================
        // RELOAD
        // =========================

        const handleReload = () => {

            const {
                loadingClients
            } = useLoadingStore.getState();

            if (loadingClients) {

                pendingInvalidate = true;

                return;
            }

            loadClients(socket);

        };

        // =========================
        // REALTIME
        // =========================

        const handleSync = clientes => {

            handleEvent('sync', clientes);

        };

        const handleUpsert = cliente => {

            handleEvent('upsert', cliente);

        };

        const handleDelete = clienteId => {

            handleEvent('delete', clienteId);

        };

        const handleInvalidate = () => {

            handleReload();

        };

        // =========================
        // DISCONNECT
        // =========================

        const handleDisconnect = () => {

            pendingClientEvents.length = 0;

            pendingInvalidate = false;

            clearClientsRequests();

        };

        // =========================
        // LISTENERS
        // =========================

        socket.on('disconnect', handleDisconnect);

        socket.on('clientes:progress', handleProgress);

        socket.on('clientes:done', handleDone);

        socket.on('clientes:error', handleError);

        socket.on('clientes:sync', handleSync);

        socket.on('clientes:upserted', handleUpsert);

        socket.on('clientes:deleted', handleDelete);

        socket.on('clientes:invalidate', handleInvalidate);

        return () => {

            socket.off('disconnect', handleDisconnect);

            socket.off('clientes:progress', handleProgress);

            socket.off('clientes:done', handleDone);

            socket.off('clientes:error', handleError);

            socket.off('clientes:sync', handleSync);

            socket.off('clientes:upserted', handleUpsert);

            socket.off('clientes:deleted', handleDelete);

            socket.off('clientes:invalidate', handleInvalidate);

            pendingClientEvents.length = 0;

            pendingInvalidate = false;

        };

    }, [socket]);

    useEffect(() => {

        if (
            !socket?.connected ||
            !connectionReady
        ) {
            return;
        }

        loadClients(socket);

    }, [
        socket,
        connectionReady
    ]);

}